import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  IconButton,
  Tooltip,
  Fade,
  Badge,
  Chip,
  Button,
  useTheme,
  Card,
  CardContent,
  CardActionArea,
  CardActions,
  Divider,
  List,
  ListItem,
  Stack,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DroneIcon from '@mui/icons-material/FlightTakeoff';
import RoadIcon from '@mui/icons-material/AddRoad';
import InsightsIcon from '@mui/icons-material/Insights';
import ImageIcon from '@mui/icons-material/Image';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import EngineeringIcon from '@mui/icons-material/Engineering';
import MapIcon from '@mui/icons-material/Map';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { alpha } from '@mui/material/styles';
// Import authenticated API service instead of raw axios
import { default as api } from '../../../services/api';
import AuthenticatedImage from '../../common/AuthenticatedImage'; // Import the new component

// Map inspection type to appropriate icon and color
const getInspectionConfig = (type) => {
  switch (type) {
    case 'drone':
      return {
        icon: <DroneIcon fontSize="small" sx={{ color: 'white' }} />,
        color: '#2196f3' // blue
      };
    case 'manual':
      return {
        icon: <RoadIcon fontSize="small" sx={{ color: 'white' }} />,
        color: '#9c27b0' // purple
      };
    case 'condition':
      return {
        icon: <InsightsIcon fontSize="small" sx={{ color: 'white' }} />,
        color: '#ff9800' // orange
      };
    default:
      return {
        icon: <ImageIcon fontSize="small" sx={{ color: 'white' }} />,
        color: '#757575' // grey
      };
  }
};

// Get color based on inspection status
const getStatusColor = (status) => {
  switch (status) {
    case 'draft':
      return '#9e9e9e'; // grey
    case 'pending_analyze':
      return '#2196f3'; // blue
    case 'in_progress':
      return '#ff9800'; // orange
    case 'rated':
      return '#4caf50'; // green
    default:
      return '#9e9e9e'; // grey for unknown
  }
};

// Get icon and color based on validation status
const getValidationConfig = (validationStatus) => {
  switch (validationStatus) {
    case 'ai_validated':
      return {
        icon: <SmartToyIcon fontSize="small" sx={{ fontSize: '16px' }} />,
        color: '#2196f3' // blue
      };
    case 'engineer_validated':
      return {
        icon: <EngineeringIcon fontSize="small" sx={{ fontSize: '16px' }} />,
        color: '#4caf50' // green
      };
    case 'pending':
    default:
      return {
        icon: <ImageIcon fontSize="small" sx={{ fontSize: '16px' }} />,
        color: '#9e9e9e' // grey
      };
  }
};

// Get tooltip text for validation status
const getValidationTooltip = (inspection, inspectionNumber) => {
  let statusText;
  
  switch (inspection.validationStatus) {
    case 'ai_validated':
      statusText = 'AI Validated';
      break;
    case 'engineer_validated':
      statusText = 'Engineer Validated';
      break;
    default:
      statusText = 'Not Validated';
  }
  
  return `${inspection.title} (#${inspectionNumber}) - ${statusText} - ${inspection.imageCount} images`;
};

const InspectionPanel = ({ 
  open, 
  onClose, 
  asset, 
  onSelectInspection,
  onAddInspection,
  onEditInspection,
  onPlotImages,
  onRemoveImages,
  onError,
  refreshKey = 0
}) => {
  console.log('[InspectionPanel] Component rendering or re-rendering.');
  const theme = useTheme();
  const scrollContainerRef = useRef(null);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [plottingId, setPlottingId] = useState(null);
  const [plottedInspections, setPlottedInspections] = useState([]);
  // Add state for preview images
  const [previewImages, setPreviewImages] = useState({});
  // Add state for deletion
  const [deletingId, setDeletingId] = useState(null); // Tracks which inspection is being deleted
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false); // Controls the dialog visibility
  const [inspectionToDelete, setInspectionToDelete] = useState(null); // Holds the inspection object to be deleted
  
  // Initialize with count of visible inspections
  const visibleCount = 12;

  // Fetch inspections for the selected asset
  const fetchInspections = useCallback(async () => {
    if (!asset) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch rating levels for the asset first to create a map
      let localRatingLevelsMap = {};
      if (asset.organization_id && asset.asset_type_id) {
        try {
          const schemesResponse = await api.get('/ratings/rating-scheme-for-asset', {
            params: {
              organization_id: asset.organization_id,
              asset_type_id: asset.asset_type_id
            }
          });
          if (schemesResponse.data && schemesResponse.data.levels) {
            schemesResponse.data.levels.forEach(level => {
              localRatingLevelsMap[level.id] = level.name;
            });
          }
        } catch (ratingError) {
          console.error("Could not fetch rating levels for asset:", ratingError);
          // Continue without rating names if this fails
        }
      }

      console.log(`Fetching inspections for asset ${asset.id}, refreshKey: ${refreshKey}`);
      const response = await api.get('/inspections', {
        params: { asset_id: asset.id }
      });
      
      if (Array.isArray(response.data) && response.data.length === 0) {
        setInspections([]);
        return;
      }
      
      // Transform API data to match the component's expected format
      const formattedInspections = response.data.map(apiInspection => {
        // Log the raw API inspection object to see its structure
        console.log('[InspectionPanel] Raw API inspection object:', JSON.stringify(apiInspection, null, 2));
        
        return {
          id: apiInspection.id || `temp-${Math.random()}`,
          // Ensure asset_id and organization_id are passed through
          asset_id: apiInspection.asset_id,
          organization_id: apiInspection.organization_id,
          // Map other fields, using fallbacks if necessary
          type: apiInspection.inspection_type_id || 'unknown',
          title: apiInspection.inspection_type_name || `Inspection ${apiInspection.id}`,
          description: apiInspection.notes || 'No description provided',
          date: apiInspection.inspection_date || new Date().toISOString(),
          imageCount: 0, // Initialize with 0, will be updated later
          status: apiInspection.status || 'draft',
          rating_level_name: localRatingLevelsMap[apiInspection.condition_rating_level_id] || null,
          validationStatus: apiInspection.validation_status || 'pending', // Use API field if available
          cycle: apiInspection.cycle // Added cycle mapping
        };
      });
      
      setInspections(formattedInspections);
      
      // Fetch photo counts for each inspection
      await Promise.all(
        formattedInspections.map(async (inspection) => {
          try {
            const countResponse = await api.get(`/inspections/${inspection.id}/photos/count`);
            const photoCount = countResponse.data.count;
            
            // Update the inspection with the actual photo count
            setInspections(prevInspections =>
              prevInspections.map(insp =>
                insp.id === inspection.id ? { ...insp, imageCount: photoCount } : insp
              )
            );

            // If inspection has photos, fetch a preview image
            if (photoCount > 0) {
              try {
                const photosResponse = await api.get(`/inspections/${inspection.id}/photos`);
                if (photosResponse.data && photosResponse.data.length > 0) {
                  // Get a random photo from the available photos
                  const randomIndex = Math.floor(Math.random() * photosResponse.data.length);
                  const randomPhoto = photosResponse.data[randomIndex];
                  
                  // The backend provides a clean, web-relative path which is the S3 object key.
                  // The AuthenticatedImage component expects this key directly as its src.
                  const photoPath = randomPhoto.photo_path.replace(/\\/g, '/');

                  // Store the S3 object key directly.
                  setPreviewImages(prev => ({
                    ...prev,
                    [inspection.id]: photoPath
                  }));
                }
              } catch (error) {
                console.error(`Failed to fetch preview image for inspection ${inspection.id}:`, error);
              }
            }
          } catch (error) {
            console.error(`Failed to fetch photo count for inspection ${inspection.id}:`, error);
            // Keep the default count of 0 if the request fails
          }
        })
      );
      
    } catch (err) {
      let errorMessage = 'Failed to load inspections';
      
      if (err.response) {
        errorMessage = `API Error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = 'No response received from server';
      } else {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [asset, refreshKey]);

  useEffect(() => {
    if (!open || !asset) return;
    console.log(`[InspectionPanel] useEffect triggered. Asset ID: ${asset.id}, Refresh Key: ${refreshKey}`);
    fetchInspections();
  }, [open, asset, fetchInspections, refreshKey]);
  
  // Reset plottedInspections state when panel is closed
  useEffect(() => {
    if (!open) {
      // Clear the plottedInspections state when panel is closed
      setPlottedInspections([]);
    }
  }, [open]);
  
  // Return early if panel is not open or no asset is selected
  if (!open || !asset) return null;
  
  // Handle plotting/removing images on map
  const handlePlotImages = async (inspection) => {
    if (!inspection || !inspection.id || !onPlotImages) return;

    // Check if this inspection is already plotted
    if (plottedInspections.includes(inspection.id)) {
      // If already plotted, call onRemoveImages
      console.log(`Removing images for already plotted inspection: ${inspection.id}`);
      onRemoveImages(inspection.id); // Pass inspection ID to remove specific images
      setPlottedInspections(prev => prev.filter(id => id !== inspection.id));
      setPlottingId(null); // Clear plotting ID
      return;
    }

    setPlottingId(inspection.id); // Set plotting ID to show loading state

    try {
      // Fetch all photos for this inspection
      const photosResponse = await api.get(`/inspections/${inspection.id}/photos`);
      
      if (!photosResponse.data || photosResponse.data.length === 0) {
        if (onError) onError('No photos with geo-coordinates found for this inspection.');
        setPlottingId(null);
        return;
      }

      // Filter for photos that have latitude and longitude
      const geolocatedPhotos = photosResponse.data.filter(
        photo => photo.latitude != null && photo.longitude != null
      );

      if (geolocatedPhotos.length === 0) {
        if (onError) onError('No photos with geo-coordinates found for this inspection.');
        setPlottingId(null);
        return;
      }
      
      // Prepare photo data for plotting, including drone metadata and inspection title
      const photoDataArray = geolocatedPhotos.map(photo => ({
        id: photo.id,
        photoPath: photo.photo_path,
        latitude: parseFloat(photo.latitude),
        longitude: parseFloat(photo.longitude),
        altitude: parseFloat(photo.altitude || 0),
        caption: photo.caption || `Photo ${photo.id}`,
        takenAt: photo.taken_at,
        // Drone metadata - ensure these fields exist or provide fallbacks
        isDronePhoto: photo.is_drone_photo || false,
        flightYaw: photo.flight_yaw,
        flightPitch: photo.flight_pitch,
        flightRoll: photo.flight_roll,
        gimbalYaw: photo.gimbal_yaw,
        gimbalPitch: photo.gimbal_pitch,
        gimbalRoll: photo.gimbal_roll,
        flightSpeed: photo.flight_speed,
        droneMake: photo.drone_make,
        droneModel: photo.drone_model,
        // Pass the inspection title for context
        inspectionTitle: inspection.title,
        // Include has_annotation status
        has_annotation: photo.has_annotation || false 
      }));
      
      // Call the onPlotImages prop with the prepared photo data and inspection ID
      onPlotImages(photoDataArray, inspection.id);
      
      // Add inspection to plotted list
      setPlottedInspections(prev => [...prev, inspection.id]);

    } catch (error) {
      console.error('Error fetching or plotting inspection images:', error);
      if (onError) {
        onError(`Failed to plot images: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setPlottingId(null); // Clear plotting ID
    }
  };
  
  // Add inspection handler
  const handleAddInspection = () => {
    if (onAddInspection) {
      onAddInspection(asset);
    } else {
      console.log('Add inspection for asset:', asset);
    }
  };

  // Handle inspection deletion button click
  const handleDeleteInspection = (inspection, event) => {
    // Stop event propagation to avoid selecting the inspection card
    if (event) {
      event.stopPropagation();
    }
    
    // Set the inspection to delete and open confirmation dialog
    setInspectionToDelete(inspection);
    setConfirmDeleteOpen(true);
  };

  // Close the delete confirmation dialog
  const handleCloseDeleteConfirm = () => {
    setConfirmDeleteOpen(false);
    setInspectionToDelete(null); // Clear the selected inspection
  };

  // Confirm and execute deletion
  const confirmDelete = async () => {
    if (!inspectionToDelete) return;
    
    setDeletingId(inspectionToDelete.id); // Show loading state on the button
    setConfirmDeleteOpen(false); // Close dialog
    
    try {
      // Make API call to delete the inspection
      await api.delete(`/inspections/${inspectionToDelete.id}`);
      
      // Remove from plotted inspections if it was plotted
      if (plottedInspections.includes(inspectionToDelete.id)) {
        if (onRemoveImages) {
          onRemoveImages(inspectionToDelete.id); // Call parent handler to remove markers
        }
        setPlottedInspections(prev => prev.filter(id => id !== inspectionToDelete.id));
      }
      
      // Remove from inspections list in state
      setInspections(prevInspections => 
        prevInspections.filter(insp => insp.id !== inspectionToDelete.id)
      );
      
      // Also remove the preview image if it exists
      setPreviewImages(prev => {
        const newImages = { ...prev };
        delete newImages[inspectionToDelete.id];
        return newImages;
      });
      
      // Optional: Show success notification via parent
      // if (onSuccess) { 
      //   onSuccess('Inspection deleted successfully');
      // }
      
    } catch (error) {
      console.error('Error deleting inspection:', error);
      let errorMessage = 'Failed to delete inspection';
      
      if (error.response) {
        errorMessage = `Error: ${error.response.status} - ${error.response.data?.message || 'Unknown API error'}`;
      }
      
      // Show error notification via parent or local state
      if (onError) {
        onError(errorMessage);
      } else {
        setError(errorMessage); // Fallback to local error state
      }
    } finally {
      setDeletingId(null); // Clear loading state
      setInspectionToDelete(null); // Clear selected inspection
    }
  };

  return (
    <Fade in={open}>
      <Paper
        elevation={4}
        sx={{
          position: 'absolute',
          top: 80,
          left: 16,
          width: 360, // Increased from 300 to 360
          maxWidth: '95vw',
          maxHeight: 'calc(100vh - 160px)',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: (theme) => theme.shadows[4],
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: '#90caf9',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ 
            px: 2,
            py: 1.5,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ overflow: 'hidden' }}>
            <Typography variant="subtitle1" sx={{
              fontWeight: 600,
              fontSize: '0.9rem',
              flexShrink: 0
            }}>
              Timeline
            </Typography>
            <Chip
              label={`${inspections.length}`}
              size="small"
              color="primary"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 500,
                flexShrink: 0
              }}
            />
            <Tooltip title={asset?.name || ''} placement="top-start">
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '150px', // Adjust this value as needed
                  cursor: 'default'
                }}
              >
                {asset?.name}
              </Typography>
            </Tooltip>
          </Stack>
          
          <IconButton 
            size="small" 
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        
        {/* Timeline area with white background */}
        <Box 
          ref={scrollContainerRef}
          sx={{ 
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            bgcolor: 'background.paper',
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: alpha('#1976d2', 0.2),
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: alpha('#1976d2', 0.5),
            },
          }}
        >
          {/* Content */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'error.main', mb: 2 }}>
                {error}
              </Typography>
              <Button
                startIcon={<RefreshIcon />}
                variant="outlined"
                size="small"
                onClick={fetchInspections}
                sx={{ mt: 1 }}
              >
                Retry
              </Button>
            </Box>
          ) : inspections.length > 0 ? (
            <List sx={{ p: 0.75 }}>
              {inspections.map((inspection, index) => (
                <ListItem 
                  key={inspection.id} 
                  sx={{ 
                    px: 1.5, 
                    py: 0.75, 
                    borderBottom: 'none',
                  }}
                  disablePadding
                >
                  <Card 
                    sx={{ 
                      width: '100%',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 2px 6px rgba(0,0,0,0.04)',
                      borderRadius: 2,
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : '#fff',
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.primary.dark, 0.1) 
                        : alpha(theme.palette.grey[100], 0.85),
                      borderLeft: `3px solid ${getStatusColor(inspection.status)}`,
                      '&:hover': {
                        transform: 'translateY(-2px) scale(1.01)',
                        boxShadow: '0 3px 12px rgba(32,84,160,0.1)',
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        borderLeftColor: getStatusColor(inspection.status),
                      },
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    elevation={0}
                  >
                    {/* Image Preview Section */}
                    {previewImages[inspection.id] && (
                      <Box
                        onClick={() => onSelectInspection(inspection)}
                        sx={{ 
                          height: 140, 
                          width: '100%', 
                          overflow: 'hidden',
                          position: 'relative',
                          cursor: 'pointer',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '30%',
                            background: 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)',
                            pointerEvents: 'none'
                          }
                        }}
                      >
                        <AuthenticatedImage
                          src={previewImages[inspection.id]}
                          alt={inspection.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.5s ease',
                            '&:hover': {
                              transform: 'scale(1.05)'
                            }
                          }}
                        />
                        <Box 
                          sx={{ 
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                            zIndex: 2
                          }}
                        >
                          <Chip 
                            label={`${inspection.imageCount}`}
                            size="small"
                            color="primary"
                            icon={<ImageIcon style={{ fontSize: '0.7rem' }} />}
                            sx={{ 
                              height: 20, 
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              backdropFilter: 'blur(4px)',
                              '& .MuiChip-label': { 
                                px: 0.7, 
                                fontSize: '0.7rem',
                                fontWeight: 500 
                              },
                              '& .MuiChip-icon': {
                                color: 'white',
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    )}

                    <CardActionArea 
                      onClick={() => onSelectInspection(inspection)}
                      sx={{ 
                        position: 'relative'
                      }}
                    >
                      <CardContent sx={{ p: 1.5, pl: 2, pb: '12px !important' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          {/* Redesigned date and ID section */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.75
                          }}>
                            {/* Date with calendar icon */}
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              bgcolor: alpha(theme.palette.primary.main, 0.06),
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                            }}>
                              <CalendarTodayIcon 
                                sx={{ 
                                  fontSize: '0.8rem', 
                                  color: alpha(theme.palette.primary.main, 0.8),
                                  mr: 0.5
                                }} 
                              />
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.675rem',
                                  color: alpha(theme.palette.primary.main, 0.9),
                                  letterSpacing: '0.01em'
                                }}
                              >
                                {new Date(inspection.date).toLocaleDateString(undefined, {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </Typography>
                            </Box>
                            
                            {/* Status Chip */}
                            <Chip
                              label={
                                inspection.status === 'rated' && inspection.rating_level_name
                                  ? `Rated (${inspection.rating_level_name})`
                                  : (inspection.status ? inspection.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown')
                              }
                              size="small"
                              sx={{
                                height: 'auto',
                                fontSize: '0.625rem',
                                fontWeight: 500,
                                borderRadius: '6px',
                                p: '3px 6px',
                                // Use status-specific colors that match the top border
                                bgcolor: alpha(getStatusColor(inspection.status), 0.1),
                                color: alpha(getStatusColor(inspection.status), 0.9),
                                border: `1px solid ${alpha(getStatusColor(inspection.status), 0.2)}`,
                              }}
                            />
                            
                            {/* ID pill */}
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1,
                                py: 0.3,
                                fontSize: '0.625rem',
                                fontWeight: 600,
                                borderRadius: '12px',
                                bgcolor: alpha(theme.palette.grey[500], 0.08),
                                color: theme.palette.text.secondary,
                                border: `1px solid ${alpha(theme.palette.grey[500], 0.1)}`,
                                fontFamily: 'monospace'
                              }}
                            >
                              {inspection.id}
                            </Box>
                          </Box>

                          {!previewImages[inspection.id] && (
                            <Chip 
                              label={`${inspection.imageCount}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                              icon={<ImageIcon style={{ fontSize: '0.7rem' }} />}
                              sx={{ 
                                height: 16, 
                                '& .MuiChip-label': { 
                                  px: 0.5, 
                                  fontSize: '0.625rem',
                                  fontWeight: 600,
                                },
                                '& .MuiChip-icon': {
                                  ml: 0.5,
                                  mr: -0.3
                                },
                                borderColor: alpha(theme.palette.primary.main, 0.4)
                              }}
                            />
                          )}
                        </Box>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontSize: '0.85rem', 
                            mb: 0.5,
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            letterSpacing: '-0.01em'
                          }}
                        >
                          {inspection.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.7rem',
                            color: alpha(theme.palette.text.secondary, 0.9),
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            lineHeight: '1.3em',
                            height: '2.6em'
                          }}
                        >
                          {inspection.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                    <Box 
                      sx={{ 
                        height: '1px', 
                        background: `linear-gradient(to right, transparent, ${alpha(theme.palette.divider, 0.3)}, transparent)`,
                        mx: 2 
                      }}
                    />
                    <CardActions 
                      sx={{ 
                        p: 0.7, 
                        pl: 1.3, 
                        pr: 1, 
                        justifyContent: 'space-between',
                        bgcolor: alpha(theme.palette.mode === 'dark' 
                          ? theme.palette.background.paper 
                          : theme.palette.grey[50], 0.7),
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: theme.palette.text.secondary,
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '0.65rem',
                            fontWeight: 400
                          }}
                        >
                          <CalendarTodayIcon 
                            sx={{ 
                              fontSize: '0.8rem', 
                              mr: 0.5, 
                              color: alpha(theme.palette.text.secondary, 0.65) 
                            }} 
                          />
                          {inspection.cycle ? `Cycle ${inspection.cycle}, ${new Date(inspection.date).getFullYear()}` : 'No cycle assigned'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}> {/* Use gap for spacing */} 
                        {/* Delete Button */}
                        <Tooltip title="Delete inspection" arrow>
                          {/* Wrapper Box to handle disabled state tooltip */}
                          <Box sx={{ display: 'inline-block' }}> 
                            <IconButton 
                              size="small" 
                              color="error" // Use error color for delete
                              disabled={deletingId === inspection.id} // Disable while deleting this specific one
                              onClick={(e) => handleDeleteInspection(inspection, e)} // Call delete handler
                              sx={{
                                width: 24,
                                height: 24,
                                p: 0.1,
                                bgcolor: 'transparent',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.08) // Subtle hover effect
                                },
                                '&.Mui-disabled': {
                                  opacity: 0.4
                                }
                              }}
                            >
                              {/* Show progress spinner when deleting */}
                              {deletingId === inspection.id ? (
                                <CircularProgress size={14} color="error" />
                              ) : (
                                <DeleteIcon sx={{ fontSize: '0.95rem' }} />
                              )}
                            </IconButton>
                          </Box>
                        </Tooltip>
                        
                        {/* Edit Button */}
                        <Tooltip title="Edit inspection" arrow>
                          <Box sx={{ display: 'inline-block' }}>
                            <IconButton
                              size="small"
                              color="default"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onEditInspection) onEditInspection(inspection);
                              }}
                              sx={{
                                width: 24,
                                height: 24,
                                p: 0.1,
                                bgcolor: 'transparent',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.info.main, 0.08)
                                }
                              }}
                            >
                              <EditIcon sx={{ fontSize: '0.95rem' }} />
                            </IconButton>
                          </Box>
                        </Tooltip>

                        {/* Existing Plot Button */}
                        <Tooltip title={plottedInspections.includes(inspection.id) ? "Remove photos from map" : "Plot photos on map"} arrow>
                          <Box sx={{ display: 'inline-block' }}> {/* Wrapper to handle disabled state */}
                            <IconButton 
                              size="small" 
                              color={plottedInspections.includes(inspection.id) ? "primary" : "default"}
                              disabled={plottingId !== null || inspection.imageCount === 0}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlotImages(inspection);
                              }}
                              sx={{ // Keep existing styles for plot button
                                width: 24,
                                height: 24,
                                p: 0.1,
                                bgcolor: plottedInspections.includes(inspection.id) 
                                  ? alpha(theme.palette.primary.main, 0.08)
                                  : 'transparent',
                                '&:hover': {
                                  bgcolor: plottedInspections.includes(inspection.id) 
                                    ? alpha(theme.palette.primary.main, 0.15)
                                    : alpha(theme.palette.action.hover, 0.7)
                                },
                                '&.Mui-disabled': {
                                  opacity: 0.4
                                }
                              }}
                            >
                              {plottingId === inspection.id ? (
                                <CircularProgress size={14} />
                              ) : plottedInspections.includes(inspection.id) ? (
                                <LocationOnIcon sx={{ fontSize: '1rem' }} color="primary" />
                              ) : (
                                <LocationOnIcon sx={{ fontSize: '1rem' }} />
                              )}
                            </IconButton>
                          </Box>
                        </Tooltip>
                      </Box>
                    </CardActions>
                  </Card>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
              <SearchOffIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" sx={{ mb: 1 }}>
                No inspections found for this asset
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.disabled' }}>
                Use the button below to add your first inspection
              </Typography>
            </Box>
          )}
        </Box>

        {/* Add inspection button */}
        <Button
          fullWidth
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          onClick={handleAddInspection}
          sx={{ 
            py: 1.5,
            borderRadius: 0,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            bgcolor: '#1976d2',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#1565c0',
              boxShadow: 'none',
            }
          }}
        >
          Add New Inspection
        </Button>
        
        {/* --- DELETE CONFIRMATION DIALOG --- */}
        <Dialog
          open={confirmDeleteOpen}
          onClose={handleCloseDeleteConfirm}
          aria-labelledby="delete-confirmation-title"
          aria-describedby="delete-confirmation-description"
          TransitionComponent={Fade} // Add smooth fade transition
          transitionDuration={{ enter: 400, exit: 300 }}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: '0 10px 35px rgba(0,0,0,0.18)',
              maxWidth: '420px',
              width: '100%',
              m: 2,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`
            }
          }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0,0,0,0.65)',
              backdropFilter: 'blur(3px)'
            }
          }}
        >
          {/* Header with icon and title */}
          <Box 
            sx={{ 
              p: 2.5, 
              pb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.error.main, 0.12),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.error.main,
                flexShrink: 0
              }}
            >
              <DeleteIcon sx={{ fontSize: '1.4rem' }} />
            </Box>
            <DialogTitle 
              id="delete-confirmation-title"
              sx={{ 
                p: 0, 
                m: 0, 
                fontWeight: 600, 
                fontSize: '1.15rem', 
                color: theme.palette.text.primary,
                lineHeight: 1.3
              }}
            >
              Confirm Deletion
            </DialogTitle>
          </Box>

          {/* Content with warning message */}
          <DialogContent 
            sx={{ 
              px: 2.5, 
              py: 1,
              pb: 2 
            }}
          >
            <DialogContentText 
              id="delete-confirmation-description"
              sx={{
                color: theme.palette.text.primary,
                mb: 2,
                fontSize: '0.95rem'
              }}
            >
              Are you sure you want to permanently delete this inspection
              {inspectionToDelete?.title ? ` "${inspectionToDelete.title}"` : ''}?
            </DialogContentText>
            
            {/* Warning about associated images */}
            {inspectionToDelete?.imageCount > 0 && (
              <Box
                sx={{
                  p: 2,
                  mb: 1,
                  borderRadius: '8px',
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5
                }}
              >
                <Box 
                  component="span" 
                  sx={{ 
                    color: theme.palette.error.main,
                    mt: 0.2
                  }}
                >
                  <WarningIcon fontSize="small" />
                </Box>
                <Typography
                  variant="body2"
                  color="error.dark"
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    lineHeight: 1.5
                  }}
                >
                  This will also delete {inspectionToDelete.imageCount} associated {inspectionToDelete.imageCount === 1 ? 'image' : 'images'}.
                </Typography>
              </Box>
            )}
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: alpha(theme.palette.text.secondary, 0.9),
                mt: 2,
                fontSize: '0.875rem'
              }}
            >
              This action cannot be undone.
            </Typography>
          </DialogContent>

          {/* Action buttons */}
          <DialogActions 
            sx={{ 
              px: 2.5, 
              py: 2,
              pt: 1.5,
              gap: 1.5
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCloseDeleteConfirm}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                py: 0.75,
                borderColor: alpha(theme.palette.divider, 0.8),
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: alpha(theme.palette.divider, 1),
                  bgcolor: alpha(theme.palette.action.hover, 0.04)
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={confirmDelete}
              disableElevation
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 0.75,
                bgcolor: theme.palette.error.main,
                '&:hover': {
                  bgcolor: theme.palette.error.dark,
                },
                transition: 'background-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 0.8
              }}
              startIcon={<DeleteIcon sx={{ fontSize: '1rem' }} />}
            >
              Delete Inspection
            </Button>
          </DialogActions>
        </Dialog>
        {/* --- END DELETE CONFIRMATION DIALOG --- */}
      </Paper>
    </Fade>
  );
};

export default memo(InspectionPanel);